import React, { useMemo, useState, memo } from 'react';
import { makeStyles } from 'src/makeStyles';
import Checkbox from '@mui/material/Checkbox';
import { useSelector } from 'react-redux';
import { FormControlLabel } from '@mui/material';
import sortBy from 'lodash/sortBy';
import { useArtistsSearchById } from 'utils/queries/profiles/artists';
import { colors } from 'constants/styleConstants';
import SimpleTable from 'components/FbTable/SimpleTable';
import SubTitle from 'components/SubTitle';
import { useTranslation } from 'src/i18n';
import TextButton from 'components/TextButton';
import ActionStatusModal from 'components/ActionStatusModal';
import PreviewCard, { SIZE } from 'components/Globals/PreviewCard';
import Button from 'components/Button';
import { TP } from 'constants/index';
import { ENTITY_TYPES } from 'operabase-router/constants';
import { AGENCY_ACTORS } from 'constants/consts';
import RequestAccess from 'components/RequestArtistAccess';
import { SECTIONS } from 'components/Globals/Analytics/constants';
import queries from 'containers/Accounts/queries';
import managerQueries from 'containers/Managers/queries';
import { useMutation } from 'utils/react-query';
import usePageContext from 'utils/hooks/usePageContext';
import { useManagerConnections } from 'utils/hooks/useManagerQueries';
import { useUserProfiles, useProfileAccesses } from 'utils/hooks/useAuthenticatedUser';
import { selectArtistsArray } from '../selectors';
import { ArtistsRow, ArtistsCard } from './ArtistRosterElements';

const MemoisedEntityCard = memo(
  PreviewCard,
  (prevProps, nextProps) => prevProps.entity.id === nextProps.entity.id && prevProps.size === nextProps.size,
);

const useStyles = makeStyles()(theme => ({
  toolBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  rosterSection: {
    marginTop: '30px',
  },
  artistRosterTable: {
    marginTop: '20px',
  },
  warningApproveButton: {
    color: colors.text,
    fontWeight: 'bold',
    padding: '0.73rem',
  },
  badge: {
    color: colors.black,
    backgroundColor: 'rgb(238, 238, 238)',
    height: 20,
    width: 35,
    alignItems: 'center',
    borderRadius: 10,
    display: 'inline-flex',
    justifyContent: 'center',
  },
  sectionTitle: {
    fontWeight: 'normal',
    display: 'inline-flex',
  },
  artistRosterPublicView: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
  },
}));

const trackingDataAgencyRoster = {
  section: SECTIONS.AGENCY_ROSTER,
};

const AgencyRoster = ({ connections, editMode, agencyData, elementRefs, isEditPanelVisible, addToRosterButton }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { navigate } = usePageContext();
  const userProfiles = useUserProfiles();
  const { unacknowledged, organizations, ...rest } = connections;
  const [isDeleteModalOpened, setModalOpenedState] = useState(false);
  const [showAdditionalAgents, setAdditionalAgents] = useState(false);
  const [artistIdToDelete, setArtistIdToDelete] = useState(null);
  const artistsArray = useSelector(selectArtistsArray());
  const profiles = useArtistsSearchById(artistsArray.map(item => item.artist?.profile?.id));
  const { data: profileAccesses } = useProfileAccesses();

  const { refetch: refetchManagerConnections } = useManagerConnections({ enabled: false });
  const { mutate: deleteProfileAccess } = useMutation(queries.deleteProfileAccess());

  const cellsNames = [`${TP}.FN_NAME`, `${TP}.m_REPRESENTATIONDETAIL`];
  if (showAdditionalAgents) {
    cellsNames.push(`${TP}.m_OTHERMANAGERSOFTHISARTIST`);
  }
  if (editMode) {
    cellsNames.push('');
  }

  const cancelRemovingArtist = () => {
    setArtistIdToDelete(null);
    setModalOpenedState(false);
  };

  const removeArtistAction = artist => {
    setArtistIdToDelete(artist);
    setModalOpenedState(true);
  };
  const { mutate: deleteEntityFromRoster } = useMutation(managerQueries.deleteEntityFromRoster());

  const deleteArtistFromRoster = (e, artistId) => {
    deleteEntityFromRoster(
      {
        queryParams: {
          actor: AGENCY_ACTORS.AGENCY,
        },
        endpointParams: {
          id: agencyData?.id,
          connectionId: artistId || artistIdToDelete?.id,
        },
      },
      {
        onSuccess: () => {
          refetchManagerConnections();
          const profileAccess = profileAccesses?.data?.find(
            item => item?.profile?.id === artistIdToDelete?.artist?.profile?.id,
          );

          if (profileAccess) {
            deleteProfileAccess({
              endpointParams: {
                profileAccessId: profileAccess?.id,
              },
            });
          }
        },
      },
    );
    cancelRemovingArtist();
  };

  const agencyLinkProps = useMemo(
    () =>
      navigate.getLinkProps({
        entityType: agencyData?.organizationType?.id === 70 ? ENTITY_TYPES.ORGANIZATION : ENTITY_TYPES.AGENCY,
        entity: agencyData,
      }),
    [navigate, agencyData],
  );

  const [requestTargetProfile, setRequestTargetProfile] = useState(null);
  const onRequestModalClose = () => {
    setRequestTargetProfile(null);
  };

  const connectionsCount = useMemo(() => Object.values(connections).reduce((acc, group) => acc + group.length, 0), [
    connections,
  ]);

  return (
    <>
      <RequestAccess
        targetProfile={requestTargetProfile}
        isModalOpen={!!requestTargetProfile}
        onClose={onRequestModalClose}
        agency={agencyData}
      />

      <div className={classes.toolBar}>
        <div>
          <SubTitle className={classes.sectionTitle}>{t(`${TP}.LP_AGENCY_ROSTER`)}</SubTitle>
          <p className={classes.badge}>{connectionsCount}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {connectionsCount > 0 && editMode && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={showAdditionalAgents}
                  onChange={() => setAdditionalAgents(!showAdditionalAgents)}
                  name="additionalAgents"
                  color="default"
                />
              }
              label={t(`${TP}.m_SHOWOTHERMANAGERSOFARTISTS`)}
            />
          )}
          {addToRosterButton}
        </div>
      </div>

      {unacknowledged?.length > 0 && editMode && (
        <section className={classes.rosterSection}>
          <SubTitle tagName="h3">{t(`${TP}.FARTIST_ROSTER_REQUESTS`)}</SubTitle>
          <div className={classes.artistRosterTable}>
            <SimpleTable
              headers={cellsNames.map(name => t(name))}
              customElements={{
                row: (
                  <ArtistsRow
                    showAdditionalAgents={showAdditionalAgents}
                    addMode={editMode}
                    t={t}
                    deleteArtistClick={removeArtistAction}
                    agencyLinkProps={agencyLinkProps}
                    userProfiles={userProfiles}
                    artistProfiles={profiles?.data?.data}
                    onRequest={setRequestTargetProfile}
                    deleteArtistFromRoster={deleteArtistFromRoster}
                    trackingData={trackingDataAgencyRoster}
                  />
                ),
                card: (
                  <ArtistsCard
                    showAdditionalAgents={showAdditionalAgents}
                    addMode={editMode}
                    t={t}
                    deleteArtistClick={removeArtistAction}
                    agencyLinkProps={agencyLinkProps}
                    userProfiles={userProfiles}
                    artistProfiles={profiles?.data?.data}
                    onRequest={setRequestTargetProfile}
                    deleteArtistFromRoster={deleteArtistFromRoster}
                    trackingData={trackingDataAgencyRoster}
                  />
                ),
              }}
              data={unacknowledged}
            />
          </div>
        </section>
      )}

      {organizations?.length > 0 && (
        <section className={classes.rosterSection}>
          <SubTitle tagName="h3">{t(`${TP}.FN_ORGANIZATIONS`)}</SubTitle>
          <div className={classes.artistRosterTable}>
            {isEditPanelVisible ? (
              <SimpleTable
                headers={cellsNames.map(name => t(name))}
                customElements={{
                  row: (
                    <ArtistsRow
                      showAdditionalAgents={showAdditionalAgents}
                      editMode={editMode}
                      t={t}
                      deleteArtistClick={removeArtistAction}
                      agencyLinkProps={agencyLinkProps}
                      userProfiles={userProfiles}
                      artistProfiles={profiles?.data?.data}
                      onRequest={setRequestTargetProfile}
                      deleteArtistFromRoster={deleteArtistFromRoster}
                      trackingData={trackingDataAgencyRoster}
                    />
                  ),
                  card: (
                    <ArtistsCard
                      showAdditionalAgents={showAdditionalAgents}
                      editMode={editMode}
                      t={t}
                      deleteArtistClick={removeArtistAction}
                      agencyLinkProps={agencyLinkProps}
                      userProfiles={userProfiles}
                      artistProfiles={profiles?.data?.data}
                      onRequest={setRequestTargetProfile}
                      deleteArtistFromRoster={deleteArtistFromRoster}
                      trackingData={trackingDataAgencyRoster}
                    />
                  ),
                }}
                data={sortBy(organizations, item => item?.organizations?.name)}
              />
            ) : (
              <div className={classes.artistRosterPublicView}>
                {organizations?.map(data => (
                  <MemoisedEntityCard
                    key={data?.organization?.id}
                    entity={data?.organization}
                    entityType={ENTITY_TYPES.ORGANIZATION}
                    // primaryProfession={data?.artist?.profile?.profession}
                    size={SIZE.LARGE}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {Object.keys(rest).map(groupIdentifier => {
        const groupList = rest[groupIdentifier];

        if (!groupList?.length) {
          return null;
        }

        const listAlphabeticOrder = sortBy(groupList, item => item?.artist?.profile?.lastName);

        const groupName = groupList?.[0]?.groupName;
        return (
          <section id={groupName} key={`section-${groupName}`} className={classes.rosterSection}>
            <SubTitle tagName="h3">
              <span
                ref={el => {
                  if (el) {
                    // eslint-disable-next-line no-param-reassign
                    elementRefs.current[groupName] = el;
                  }
                }}
                data-name={groupName}
              >
                {groupName}
              </span>
            </SubTitle>
            <div className={classes.artistRosterTable}>
              {isEditPanelVisible ? (
                <SimpleTable
                  headers={cellsNames.map(name => t(name))}
                  customElements={{
                    row: (
                      <ArtistsRow
                        showAdditionalAgents={showAdditionalAgents}
                        editMode={editMode}
                        t={t}
                        deleteArtistClick={removeArtistAction}
                        agencyLinkProps={agencyLinkProps}
                        userProfiles={userProfiles}
                        artistProfiles={profiles?.data?.data}
                        onRequest={setRequestTargetProfile}
                        deleteArtistFromRoster={deleteArtistFromRoster}
                        trackingData={trackingDataAgencyRoster}
                      />
                    ),
                    card: (
                      <ArtistsCard
                        showAdditionalAgents={showAdditionalAgents}
                        editMode={editMode}
                        t={t}
                        deleteArtistClick={removeArtistAction}
                        agencyLinkProps={agencyLinkProps}
                        userProfiles={userProfiles}
                        artistProfiles={profiles?.data?.data}
                        onRequest={setRequestTargetProfile}
                        deleteArtistFromRoster={deleteArtistFromRoster}
                        trackingData={trackingDataAgencyRoster}
                      />
                    ),
                  }}
                  data={listAlphabeticOrder}
                />
              ) : (
                <div className={classes.artistRosterPublicView}>
                  {groupList.map(data => (
                    <MemoisedEntityCard
                      key={data.artist.profile.id}
                      entity={data.artist.profile}
                      entityType={ENTITY_TYPES.PROFILE}
                      primaryProfession={data?.artist?.profile?.profession}
                      size={SIZE.LARGE}
                    />
                  ))}
                </div>
              )}
            </div>
          </section>
        );
      })}
      <ActionStatusModal
        isOpened={isDeleteModalOpened}
        onClose={() => setModalOpenedState(false)}
        type="warning"
        title={t(`${TP}.PAYMENT_DETAILS_DIALOG_DELETE_TITLE`)}
        buttons={[
          <TextButton onClick={deleteArtistFromRoster} className={classes.warningApproveButton} key="approve-button">
            {`${t(`${TP}.m_YES`)}, ${t(`${TP}.m_DELETE`)}`}
          </TextButton>,
          <Button onClick={cancelRemovingArtist} key="cancel-button">
            {t(`${TP}.m_CANCEL`)}
          </Button>,
        ]}
      >
        <span>{t(`${TP}.AGENCY_DIALOG_DELETE_SUBTITLE`)}</span>
      </ActionStatusModal>
    </>
  );
};

export default AgencyRoster;
